import { useContext, useState } from "react";
import { getSiteTasks, getMasterTask, manageTask, deleteTask, getTaskById, fetchComments, postComment, taskAction, getTaskFiles, postTaskFiles, deleteTaskFiles, patchTaskById, postTask, getMasterTaskById, postCreatedFromMasterTask, deleteTaskById, bulkDeleteTasks } from "../../../Services/siteServices";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "../Constants";
import { SiteContext } from "../../../Context";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

export const useTasks = ({ fetch = true, pagination = true } = {}) => {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({ status: null });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(pagination ? 10 : 0 );
  const { site } = useContext(SiteContext);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const onPageChange = (newPage) => setPage(newPage);

  const onPageSizeChange = (newSize) => {
    setRowsPerPage(newSize);
    setPage(1);
  };

  const fetchTasksBySiteId = async (params) => {
    setLoading(true);
    try {
      const res = await getSiteTasks(
        site.id,
        {
          ...filters,
          limit: rowsPerPage,
          offset: (page - 1) * rowsPerPage,
          ...params
        });
      if (res.status === 200) {
        setTasks(res?.data?.results || res?.data);
        setTotalPages(Math.max(1, Math.ceil(res.data.count / rowsPerPage)));
        setError(null);
        return res.status;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.TASK_FETCH_FAILED;
        toast.error(msg);
        return res.status;
      }
    } catch (err) {
      setError(TOAST_MESSAGES.TASK_FETCH_FAILED);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterTask = async () => {
    setLoading(true);
    try {
      const res = await getMasterTask({project_type: site.project_type, project_category: site.project_category});
      if (res.status === 200) {
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.TASK_FETCH_FAILED;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.TASK_FETCH_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const fetchMasterTaskById = async (taskId) => {
    setLoading(true);
    try {
      const res = await getMasterTaskById(taskId);
      if (res.status === 200) {
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.TASK_FETCH_FAILED;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.TASK_FETCH_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const fetchTaskById = async (taskId) => {
    setLoading(true);
    try {
      const res = await getTaskById(taskId);
      if (res.status === 200) {
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.TASK_FETCH_FAILED;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.TASK_FETCH_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const addTask = async (data) => {
    setLoading(true);
    try {
      const res = await postTask({...data, site: site.id});
      if (res.status === 201) {
        return res.data;
      } else {
        const msg = extractErrorMessage(res) || TOAST_MESSAGES.TASK_ADD_FAILED;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.TASK_ADD_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const editTaskById = async (taskId, data) => {
    setLoading(true);
    try {
      const res = await patchTaskById(taskId, data);
      if (res.status === 200) {
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.TASK_FETCH_FAILED;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.TASK_FETCH_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const ManageTask = async (data) => {
    setLoading(true);
    data = {
      site: site.id,
      task_data: data
    }
    try {
      const res = await manageTask(data);
      if (res.status === 200) {
        return res;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_TO_UPDATE_TASK;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_TO_UPDATE_TASK;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const createFormMaster = async (data) => {
    setLoading(true);
    try {
      const payload = { 
        task_ids: Array.isArray(data) ? data : [data], 
        site: site.id 
      };

      const res = await postCreatedFromMasterTask(payload);

      if (res.status === 201) {
        return res;
      } else {
        const msg = extractErrorMessage(res) || TOAST_MESSAGES.TASK_ADD_FAILED;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.TASK_ADD_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  // For master tasks
  const removeTask = async (title) =>{
    setLoading(true);
    const data = {
      site: site.id,
      task_title: title
    }
    try {
      const res = await deleteTask(data);
      if (res.status === 200) {
        fetchTasksBySiteId();
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_TO_UPDATE_TASK;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_TO_UPDATE_TASK;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  // for master tasks bulk removal 
  const removeTasksBulk = async (titles = []) => {
    setLoading(true);
    const data = {
      site: site.id,
      task_titles: titles,
    };

    try {
      const res = await bulkDeleteTasks(data);
      if (res.status === 200) {
        await fetchTasksBySiteId();
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_TO_UPDATE_TASK;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      toast.error(extractErrorMessage(error) || TOAST_MESSAGES.FAILED_TO_UPDATE_TASK);
    } finally {
      setLoading(false);
    }
  };


  // for site tasks
  const removeTaskById = async (taskId) =>{
    setLoading(true);
    try {
      const res = await deleteTaskById(taskId);
      if (res.status === 204) {
        fetchTasksBySiteId();
        return res.data;
      } else {
        toast.error(TOAST_MESSAGES.FAILED_TO_UPDATE_TASK);
        return null;
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.FAILED_TO_UPDATE_TASK);
    } finally {
      setLoading(false);
    }
  }

  const addComment = async (taskId, comment) => {
    setLoading(true);
    try {
      const res = await postComment(taskId, { comment });
      if (res.status === 201) {
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.COMMENT_ADD_FAILED;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.COMMENT_ADD_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getComments = async (taskId) => {
    setLoading(true);
    try {
      const res = await fetchComments(taskId, {
          ...filters,
          limit: rowsPerPage,
          offset: (page - 1) * rowsPerPage,
        });
      if (res.status === 200) {
        setTotalPages(Math.max(1, Math.ceil(res.data.count / rowsPerPage)));
        return res.data.results;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.COMMENT_FETCH_FAILED;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.COMMENT_FETCH_FAILED;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const taskActions = async (taskId, action) => {
    setLoading(true);
    try {
      const res = await taskAction(taskId, { action });
      if (res.status === 200) {
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_TO_UPDATE_TASK;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_TO_UPDATE_TASK;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const fetchFiles = async (taskId) => {
    setLoading(true);
    try {
      const res = await getTaskFiles(
        taskId,
        {
          ...filters,
          limit: rowsPerPage,
          offset: (page - 1) * rowsPerPage,
        });
      if (res.status === 200) {
        setTotalPages(Math.max(1, Math.ceil(res.data.count / rowsPerPage)));
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_FETCH_FILE;
        toast.error(msg);
        return null;
      }
    } catch (err) {
      setError(TOAST_MESSAGES.FAILED_FETCH_FILE);
    } finally {
      setLoading(false);
    }
  }

  const uploadFile = async (taskId, data) => {
    setLoading(true);
    try {
      const res = await postTaskFiles(taskId, data);
      if (res.status === 201) {
        toast.success(TOAST_MESSAGES.FILE_UPLOADED);
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_UPLOAD_FILE;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_UPLOAD_FILE;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const deleteTaskFile = async (taskId, fileId) => {
    setLoading(true);
    try {
      const res = await deleteTaskFiles(taskId, fileId);
      if (res.status === 200 ) {
        toast.success(TOAST_MESSAGES.FILE_DELETED);
        return res.data;
      } else {
        const msg =
          extractErrorMessage(res) || TOAST_MESSAGES.FAILED_DELETE_FILE;
        toast.error(msg);
        return null;
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.FAILED_DELETE_FILE;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return {
    loading,
    fetchTasksBySiteId,
    setFilters,
    tasks,
    filters,
    page,
    totalPages,
    rowsPerPage,
    onPageChange,
    onPageSizeChange,
    fetchMasterTask,
    error,
    removeTask,
    ManageTask,
    fetchTaskById,
    addComment,
    getComments,
    taskActions,
    fetchFiles,
    uploadFile,
    deleteTaskFile,
    editTaskById,
    addTask,
    fetchMasterTaskById,
    createFormMaster,
    removeTaskById,
    removeTasksBulk
  };
};
